import { useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "@/lib/api";

export function useGameSocket({ code, playerId, role, onMessage }) {
  const wsRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const reconnectTimer = useRef(null);

  // Keep the latest onMessage in a ref so the socket always calls the CURRENT
  // handler without needing to reconnect when the parent re-renders. This is the
  // correct fix for the stale-closure: the connect effect stays keyed only on the
  // connection identity (code/playerId/role), while message handling stays fresh.
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const send = useCallback((msg) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  useEffect(() => {
    if (!code || !playerId) return;
    let closed = false;

    const connect = () => {
      setStatus("connecting");
      const ws = new WebSocket(WS_URL(code, playerId, role));
      wsRef.current = ws;
      ws.onopen = () => setStatus("open");
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          onMessageRef.current?.(data);
        } catch {}
      };
      ws.onclose = () => {
        setStatus("closed");
        if (!closed) {
          reconnectTimer.current = setTimeout(connect, 1500);
        }
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [code, playerId, role]);

  return { send, status };
}
